/**
 * Get structure options menu for draw navigation.
 * Provides options to navigate structures, edit names, and add qualifying/playoffs/consolation.
 */
import { navigateToEvent } from 'components/tables/common/navigateToEvent';
import { tools, drawDefinitionConstants } from 'tods-competition-factory';
import { editStructureNames } from 'components/modals/editStructureNames';
import { mutationRequest } from 'services/mutation/mutationRequest';
import { editGroupNames } from 'components/modals/editGroupNames';
import { addStructures } from 'components/modals/addStructures';
import { addDraw } from 'components/drawers/addDraw/addDraw';
import { tournamentEngine } from 'services/factory/engine';
import { renderDrawView } from './renderDrawView';

// Constants
import { ATTACH_CONSOLATION_STRUCTURES, REMOVE_STAGE_ENTRIES } from 'constants/mutationConstants';
import { ROUNDS_BRACKET, ROUNDS_COLUMNS } from 'constants/tmxConstants';
import { t } from 'i18n';

const { FINISHING_POSITIONS, CONTAINER, MAIN, AD_HOC, VOLUNTARY_CONSOLATION } = drawDefinitionConstants;

export function getStructureOptions({
  drawData,
  eventId,
  structureId,
}: {
  drawData: any;
  eventId: string;
  structureId: string;
}): any[] {
  if (!drawData) return [];
  const drawId = drawData.drawId;

  // Refresh draw view after structural changes (rename, add playoffs, etc.).
  // Uses eventId/drawId/structureId from this closure to avoid undefined eventId issues.
  // renderDrawView uses morphdom — only changed DOM elements update.
  const refreshAfterStructuralChange = () => {
    renderDrawView({ eventId, drawId, structureId });
  };
  const profiles = tournamentEngine.getAvailablePlayoffProfiles({ drawId, structureId });
  const canAddPlayoffs = profiles?.playoffRounds?.length || profiles?.playoffFinishingPositionRanges?.length;
  const canAddQualifying = (() => {
    if (!tournamentEngine.isValidForQualifying({ drawId, structureId })?.valid) return false;
    // Hide when all positions are already filled (participants or byes)
    const { drawDefinition } = tournamentEngine.getEvent({ drawId });
    const mainStructure = drawDefinition?.structures?.find((s: any) => s.structureId === structureId);
    const positions = mainStructure?.positionAssignments || [];
    return positions.some((p: any) => !p.participantId && !p.bye);
  })();

  const addNewQualifying = () => {
    addDraw({
      callback: (result: any) => {
        if (result.success) {
          const structureId = result.structure.structureId;
          navigateToEvent({ eventId, drawId, structureId, renderDraw: true });
        }
      },
      drawName: 'Qualifying',
      isQualifying: true,
      structureId,
      eventId,
      drawId,
    });
  };

  const structure = drawData.structures.find((structure: any) => structure.structureId === structureId);
  const isRoundRobin = structure?.structureType === CONTAINER;

  // Consolation can be added for elimination draws (SE, Lucky) — not AD_HOC or RR
  const isMainStructure = structure?.stage === MAIN && structure?.stageSequence === 1;
  const isAdHocDraw = drawData.drawType === AD_HOC;
  const hasConsolation = drawData.structures.some((s: any) => s.stage === VOLUNTARY_CONSOLATION);
  const canAddConsolation = isMainStructure && !isRoundRobin && !isAdHocDraw && !hasConsolation;

  return drawData.structures
    .toSorted((a: any, b: any) => tools.structureSort(a, b, { mode: FINISHING_POSITIONS }))
    .map((structure: any) => {
      const name = structure.structureName || structure.stage || 'Structure';
      return {
        onClick: () => {
          const roundsView = structure.structureType === CONTAINER ? ROUNDS_BRACKET : ROUNDS_COLUMNS;
          renderDrawView({ eventId, drawId, structureId: structure.structureId, redraw: true, roundsView });
        },
        label: name,
        close: true,
      };
    })
    .concat([
      { divider: true },
      {
        onClick: () => editStructureNames({ drawId, callback: refreshAfterStructuralChange }),
        label: t('modals.editStructureNames.title'),
        modifyLabel: false,
        close: true,
      },
      {
        onClick: () => editGroupNames({ drawId, structure, callback: refreshAfterStructuralChange }),
        label: t('modals.editGroupNames.title'),
        hide: !isRoundRobin,
        modifyLabel: false,
        close: true,
      },
      {
        onClick: () => addNewQualifying(),
        hide: !canAddQualifying,
        label: t('drawActions.addQualifying'),
        modifyLabel: false,
        close: true,
      },
      {
        onClick: () => (addStructures as any)({ drawId, structureId, callback: refreshAfterStructuralChange }),
        hide: !canAddPlayoffs,
        label: t('drawActions.addPlayoffs'),
        modifyLabel: false,
        close: true,
      },
      {
        onClick: () => {
          // First purge any leftover VC entries, then create empty structure
          const purgeAndCreate = () => {
            const genResult = tournamentEngine.generateVoluntaryConsolation({
              structureName: 'Consolation',
              drawId,
            });
            if (genResult.error || !genResult.structures?.length) {
              console.error('[addVC] generateVoluntaryConsolation failed:', genResult.error || 'no structures');
              return;
            }
            const vcStructureId = genResult.structures[0].structureId;
            mutationRequest({
              methods: [
                {
                  method: ATTACH_CONSOLATION_STRUCTURES,
                  params: { structures: genResult.structures, links: genResult.links, drawId },
                },
              ],
              callback: (result: any) => {
                if (result.success) {
                  navigateToEvent({ eventId, drawId, structureId: vcStructureId, renderDraw: true });
                }
              },
            });
          };

          // Check for leftover VC entries and remove them first
          const { drawDefinition: dd } = tournamentEngine.getEvent({ drawId });
          const hasVcEntries = dd?.entries?.some((e: any) => e.entryStage === VOLUNTARY_CONSOLATION);
          if (hasVcEntries) {
            mutationRequest({
              methods: [{ method: REMOVE_STAGE_ENTRIES, params: { drawId, entryStage: VOLUNTARY_CONSOLATION } }],
              callback: (result: any) => {
                if (result.success) purgeAndCreate();
              },
            });
          } else {
            purgeAndCreate();
          }
        },
        hide: !canAddConsolation,
        label: t('modals.addConsolation.title'),
        modifyLabel: false,
        close: true,
      },
    ]);
}
